import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PlanContext = createContext(null);

export function PlanProvider({ children }) {
  const { isAuthenticated, user } = useAuth();
  const [planData, setPlanData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPlan = useCallback(async () => {
    if (!isAuthenticated) {
      setPlanData(null);
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/subscription/status`, { withCredentials: true });
      setPlanData(res.data);
    } catch {
      setPlanData({ plan: "free", trial_active: false, features: [], invoice_history_limit: 50 });
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchPlan();
  }, [fetchPlan]);

  const hasFeature = useCallback((feature) => {
    if (!planData) return false;
    return planData.features?.includes(feature);
  }, [planData]);

  const isPaid = planData?.plan === "growth" || planData?.plan === "yearly";
  const isTrial = planData?.trial_active === true;
  const isFree = !isPaid && !isTrial;

  return (
    <PlanContext.Provider value={{
      plan: planData?.plan || "free",
      planData,
      loading,
      hasFeature,
      isPaid,
      isTrial,
      isFree,
      trialDaysLeft: planData?.trial_days_left || 0,
      invoiceHistoryLimit: planData?.invoice_history_limit ?? 50,
      refreshPlan: fetchPlan,
    }}>
      {children}
    </PlanContext.Provider>
  );
}

export function usePlan() {
  const context = useContext(PlanContext);
  if (!context) {
    return {
      plan: "free", planData: null, loading: true, hasFeature: () => false,
      isPaid: false, isTrial: false, isFree: true, trialDaysLeft: 0,
      invoiceHistoryLimit: 50, refreshPlan: () => {},
    };
  }
  return context;
}
